import { Show, onMount, lazy, useContext, Switch, Match, createSignal } from 'solid-js';
import { Route, Routes } from '@solidjs/router';

import { PageContainer } from '../components/page/page';
import { HeaderContainer } from '../components/header/header';
import { FooterContainer } from '../components/footer/footer';
import { AppContext } from '../app-context-provider/app-context-provider';
import { Title } from '@solidjs/meta';
import { LoginMenu } from '../components/login-menu/login-menu';
import { ErrorCatcher } from '../tools/error-catcher';
import { Announcement } from '../components/announcement/announcement';
import { LocaleController } from '../components/language-selector/locale-controller';
import { NativeAppPrompt } from '../components/native-app-prompt/native-app-prompt';
import { NewsletterSignupPromptForPdf } from '../components/pdf-builder/pdf-dashboard/pdf-newsletter-signup-prompt/pdf-newsletter-signup-prompt';
import { FooterAtosCareContainer } from '../components/footer/atos-care/footer-atos-care';
import { HeaderAtosCareContainer } from '../components/header/atos-care/header-atos-care';
import { BasketContextProvider } from '../components/basket/basket-context-provider';
import { Menu } from '../components/menu/menu';
import { HeaderCampaignContainer } from '../components/header/campaign-header/campaign-header';
import { UserProfile } from '../components/user-profile/user-profile';
import { Chatbot } from '../components/chatbot/chatbot';
import { Button } from '../ui-components/button/button';
import { Text } from '../ui-components/text/text';
import { Heading } from '../ui-components/heading/heading';
import { VerificationStatus, getVerificationMessage, VerificationModalMessage } from '../tools/verify-user';
import { Modal } from '../ui-components/modal/modal';
import { WebSiteSchema } from '../components/structured-data';
import { MAIN_CONTENT_ID, SkipLink } from '../components/skip-link/skip-link';

// Lazy load everything else
const Product = lazy(() => import('../components/product/product').then((imported) => ({ default: imported.Product })));
const Event = lazy(() => import('../components/event/event').then((imported) => ({ default: imported.Event })));
const BlogNews = lazy(() => import('../components/blog-news/blog-news').then((imported) => ({ default: imported.BlogNews })));
const Video = lazy(() => import('../components/video/video').then((imported) => ({ default: imported.Video })));
const Course = lazy(() => import('../components/course/course').then((imported) => ({ default: imported.Course })));
const Search = lazy(() => import('../components/search/search').then((imported) => ({ default: imported.Search })));
const PdfFactory = lazy(() => import('../components/pdf-builder/pdf-factory/pdf-factory').then((imported) => ({ default: imported.PdfFactory })));
const CertificateFactory = lazy(() =>
    import('../components/certificate-factory/certificate-factory').then((imported) => ({ default: imported.CertificateFactory }))
);

export const App = () => {
    const {
        productsEntityName,
        eventsEntityName,
        blogAndNewsEntityName,
        videosEntityName,
        coursesEntityName,
        searchPage,
        userState,
        isPdfGenerator,
        isCertificateGenerator,
        siteInfo,
        appStore,
        localize,
        coursesListingPage,
        userProfileUrls,
    } = useContext(AppContext);

    const [verifyModalMessage, setVerifyModalMessage] = createSignal<VerificationModalMessage>({
        headingSlug: '',
        bodySlug: '',
        defaultHeading: '',
        defaultBody: '',
    });

    const [showVerifyModal, setShowVerifyModal] = createSignal(false);

    onMount(() => {
        const params = new URLSearchParams(location.search);
        const verifyParam = params.get('verify');

        if (verifyParam) {
            const message = getVerificationMessage(verifyParam as VerificationStatus);
            setVerifyModalMessage(message);
            setShowVerifyModal(true);

            // Remove the verify param from the URL
            params.delete('verify');
            const search = params.toString();
            const newUrl = search ? `${location.pathname}?${search}` : location.pathname;
            window.history.replaceState({}, '', newUrl);
        }
    });

    const VerifyUserModal = () => (
        <Modal visible={showVerifyModal()} onClose={() => (setShowVerifyModal(false))} size='small' darkenBackground={true}>
            <div class="flex flex-col gap-cvi-6 pt-cvi-9">
                <Heading tag="h2" variant="heading-xl" noBlockSpacing>
                    {localize(verifyModalMessage().headingSlug!, verifyModalMessage().defaultHeading!)}
                </Heading>

                <Text fontSize="body-md" splitParagraph={true}>
                    {localize(verifyModalMessage().bodySlug!, verifyModalMessage().defaultBody!)}
                </Text>

                <Button
                    label={localize('close', 'Close')}
                    onClick={() => setShowVerifyModal(false)}
                    noBlockSpacing={true}
                />
            </div>
        </Modal>
    );

    return (
        <>
            <Show when={siteInfo?.siteType === 'atos-care'} fallback={<Title>Atos Medical</Title>}>
                <Title>Atos Care</Title>
            </Show>

            <WebSiteSchema />
            <div class="flex flex-col flex-1 [&_img]:max-w-full">
                <SkipLink />
                <Announcement />
                <VerifyUserModal />

                <LocaleController />

                <NativeAppPrompt />
                <NewsletterSignupPromptForPdf />

                <BasketContextProvider siteInfo={siteInfo} userState={userState}>
                    <Switch>
                        <Match when={siteInfo?.siteType === 'atos-care'}>
                            <HeaderAtosCareContainer isHcp={appStore.isHcp()} />
                        </Match>
                        <Match when={appStore.isCampaignPage()}>
                            <HeaderCampaignContainer />
                        </Match>
                        <Match when={siteInfo?.siteType === 'default'}>
                            <HeaderContainer />
                        </Match>
                    </Switch>

                    <LoginMenu />

                    <Menu />

                    <div id="pdf-factory-root" class="overflow-hidden h-0 print:h-auto" />

                    <Chatbot />

                    <ErrorCatcher componentName="App">
                        <main id={MAIN_CONTENT_ID} tabindex="-1" class="flex-1 z-[1] print:pb-0 focus:outline-none">
                            <Show when={isPdfGenerator}>
                                <PdfFactory />
                            </Show>

                            <Show when={isCertificateGenerator}>
                                <CertificateFactory />
                            </Show>

                            <Show when={!isPdfGenerator && !isCertificateGenerator}>
                                <Routes>
                                    {/*
                                    NOTE: When adding new routes here -> If you want to render a menu on the
                                    route and its subroute components, you need to render the menu component inside 
                                    a portal leading back here. We need context from the page/post to be able to 
                                    get the correct menu from the db. Hence this annoying setup.

                                    NOTE 2: We only add these dynamic routes if there's a value for the entity, otherwise 
                                    it will act as a "catch all"-route and catch stuff that we normally want our PageContainer to handle.

                                    NOTE 3: Dont forget to add menu portal when creating new routes!
                                    */}

                                    {/* Use account related routes */}
                                    <Route path={userProfileUrls.account} element={<UserProfile view="default" />} />
                                    {siteInfo?.siteType === 'atos-care' && (
                                        <Route path={userProfileUrls.orders} element={<UserProfile view="orders" />} />
                                    )}
                                    {siteInfo?.siteType === 'default' && (
                                        <Route path={userProfileUrls.bookmarks} element={<UserProfile view="bookmarks" />} />
                                    )}
                                    {siteInfo?.siteType === 'default' && coursesListingPage?.url && (
                                        <Route path={userProfileUrls.learning} element={<UserProfile view="learning" />} />
                                    )}

                                    {productsEntityName && <Route path={`/${productsEntityName}/:productSlug`} element={<Product />} />}
                                    {eventsEntityName && <Route path={`/${eventsEntityName}/:eventSlug`} element={<Event />} />}
                                    {blogAndNewsEntityName && <Route path={`/${blogAndNewsEntityName}/:postSlug`} element={<BlogNews />} />}
                                    {videosEntityName && <Route path={`/${videosEntityName}/:videoSlug`} element={<Video />} />}
                                    {coursesEntityName && <Route path={`/${coursesEntityName}/:courseId`} element={<Course />} />}
                                    {searchPage?.url !== '' && <Route path={`${searchPage?.url}`} element={<Search />} />}
                                    {/* {shop.basketPage?.url !== '' && <Route path={`${shop.basketPage?.url}`} element={<Basket />} />} */}
                                    <Route path="/*" element={<PageContainer />} />
                                </Routes>
                            </Show>
                        </main>
                    </ErrorCatcher>
                </BasketContextProvider>
                <Show when={siteInfo?.siteType === 'default'} fallback={<FooterAtosCareContainer />}>
                    <FooterContainer />
                </Show>
            </div>
        </>
    );
};
